import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  InputBase,
  Badge,
  useTheme,
  // Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  SearchTwoTone,
  NotificationsTwoTone,
  SettingsTwoTone,
  LogoutTwoTone,
  PersonTwoTone,
  PersonAddTwoTone,
  HomeTwoTone,
  GroupTwoTone,
  HelpOutline,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import { useAuth, useViewPort, useTheme as useAppTheme } from '@/hooks';
import { ThemeToggle, ConfirmDialog, Tour, type TourStep } from '@/components';
// import { useDispatch } from 'react-redux';
// import { setCustomColors } from '@/store';
import { useNavigate, useLocation } from 'react-router-dom';
import { followService, FollowRequestItem } from '../../services/follow/followService';
import { notificationService } from '../../services/notification/notificationService';

interface SocialHeaderProps {
  onMobileMenuToggle: () => void;
}

export const SocialHeader: React.FC<SocialHeaderProps> = ({
  onMobileMenuToggle,
}) => {
  const { user, logout } = useAuth();
  const { isMobile } = useViewPort();
  const theme = useTheme();
  const { mode, toggleTheme } = useAppTheme();
  // const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [requestsAnchorEl, setRequestsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [followRequests, setFollowRequests] = React.useState<FollowRequestItem[]>([]);
  const [requestsLoading, setRequestsLoading] = React.useState(false);
  const [requestsPage, setRequestsPage] = React.useState(1);
  const [hasMoreRequests, setHasMoreRequests] = React.useState(true);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = React.useState(0);
  const [tourOpen, setTourOpen] = React.useState(false);

  // Tour steps for header navigation
  const tourSteps: TourStep[] = [
    {
      title: 'Welcome to HRTalky!',
      content: 'Let me show you around the platform. This quick tour will help you discover all the features available to you.',
    },
    {
      target: '[data-tour="search"]',
      title: 'Search',
      content: 'Find people, skills, resumes, and more across the entire platform. Just type and search!',
      placement: 'bottom',
    },
    {
      target: '[data-tour="home"]',
      title: 'Home Feed',
      content: 'Return to your personalized home feed to see the latest posts and updates from your network.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="network"]',
      title: 'My Network',
      content: 'Manage your professional connections, see your followers, and discover people to follow.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="requests"]',
      title: 'Follow Requests',
      content: 'View and respond to follow requests from other users who want to connect with you.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="notifications"]',
      title: 'Notifications',
      content: 'Stay updated with notifications about likes, comments, mentions, and other activities.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="theme"]',
      title: 'Theme Toggle',
      content: 'Switch between light and dark mode for your preferred viewing experience.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="profile"]',
      title: 'Profile Menu',
      content: 'Access your profile, account settings, and logout option from this menu.',
      placement: 'bottom',
    },
  ];

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRequestsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRequestsAnchorEl(event.currentTarget);
    if (followRequests.length === 0) {
      loadFollowRequests();
    }
  };

  const handleRequestsMenuClose = () => {
    setRequestsAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleProfileMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    await logout();
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleNetworkClick = () => {
    navigate('/network');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/profile-suggestions?query=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const loadFollowRequests = async (page = 1, reset = false) => {
    try {
      setRequestsLoading(true);
      const response = await followService.getFollowRequests({
        type: 'received',
        page,
        limit: 10,
      });

      if (response.success) {
        if (reset || page === 1) {
          setFollowRequests(response.data.requests);
        } else {
          setFollowRequests(prev => [...prev, ...response.data.requests]);
        }
        setHasMoreRequests(page < response.data.pagination.pages);
        setRequestsPage(page);
      }
    } catch (error) {
      console.error('Failed to load follow requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadMoreRequests = () => {
    if (!requestsLoading && hasMoreRequests) {
      loadFollowRequests(requestsPage + 1);
    }
  };

  const loadUnreadNotificationsCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadNotificationsCount(response.data.count);
      }
    } catch (error) {
      console.error('Failed to load unread notifications count:', error);
    }
  };

  const handleRequestClick = (requesterId: string) => {
    handleRequestsMenuClose();
    navigate(`/profile/${requesterId}`);
  };

  // Poll for follow requests every 10 seconds
  React.useEffect(() => {
    // Load initial data
    loadFollowRequests(1, true);
    loadUnreadNotificationsCount();

    // Set up polling interval
    const intervalId = setInterval(() => {
      loadFollowRequests(1, true);
      loadUnreadNotificationsCount();
    }, 10000); // 10 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // const _handleColorChange = (primary: string, secondary: string) => {
  //   dispatch(setCustomColors({ primary, secondary }));
  // };

  const isMenuOpen = Boolean(anchorEl);
  const isRequestsMenuOpen = Boolean(requestsAnchorEl);

  const handleStartTour = () => {
    setTourOpen(true);
  };

  const handleCloseTour = () => {
    setTourOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 }, maxWidth: 'xl', width: '100%', mx: 'auto', minHeight: { xs: 56, sm: 60 } }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMobileMenuToggle}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo - Hidden on mobile */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={() => navigate('/talky')}
              sx={{
                width: 128,
                height: 34,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
                cursor: 'pointer',
              }}
            >
              {theme.palette.mode === 'light' ? (
                <img src="/logoNew.png" alt="Logo" style={{ width: 140, height: 48, marginBottom: 0, marginTop: 10 }} />
              ) : (
                <img src="/white.png" alt="Logo" style={{ width: 140, height: 40, marginBottom: 0, marginTop: 8 }} />
              )}
            </Box>
          </Box>
        )}

        {/* Search Bar */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            mx: { xs: 1, sm: 4 },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            data-tour="search"
            sx={{
              position: 'relative',
              borderRadius: 1,
              bgcolor: theme.palette.mode === 'light' ? '#eef3f8' : 'grey.800',
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                height: '100%',
                left: 12,
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <SearchTwoTone sx={{ color: 'text.secondary', fontSize: 18 }} />
            </Box>
            <InputBase
              placeholder={isMobile ? "Search..." : "Search people, skills, resume..."}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                color: 'inherit',
                width: '100%',
                '& .MuiInputBase-input': {
                  py: 1,
                  pl: 5,
                  pr: 2,
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </Box>

        {/* Right Side Icons */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
          {(() => {
            const navItemSx = {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 64,
              height: 56,
              px: 1,
              pt: 1.25,
              cursor: 'pointer',
              borderRadius: 0,
              '&:hover': { bgcolor: 'action.hover' },
            } as const;
            const iconRowSx = {
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            } as const;
            const labelSx = {
              fontSize: '0.7rem',
              lineHeight: 1,
              mt: 0.5,
            } as const;

            return (
              <>
                {!isMobile && (
                  <>
                    {/* Home */}
                    <Box
                      data-tour="home"
                      onClick={() => navigate('/talky')}
                      sx={{ ...navItemSx, color: location.pathname === '/talky' ? 'primary.main' : 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        <HomeTwoTone sx={{ fontSize: 22 }} />
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Home</Typography>
                    </Box>

                    {/* My Network */}
                    <Box
                      data-tour="network"
                      onClick={handleNetworkClick}
                      sx={{ ...navItemSx, color: location.pathname === '/network' ? 'primary.main' : 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        <GroupTwoTone sx={{ fontSize: 22 }} />
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Network</Typography>
                    </Box>

                    {/* Follow Requests */}
                    <Box
                      data-tour="requests"
                      onClick={handleRequestsMenuOpen}
                      sx={{ ...navItemSx, color: 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        <Badge
                          badgeContent={followRequests.filter(req => req.status === 'pending').length || null}
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.6rem',
                              height: 15,
                              minWidth: 15,
                              padding: '0 4px',
                            },
                          }}
                        >
                          <PersonAddTwoTone sx={{ fontSize: 22 }} />
                        </Badge>
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Requests</Typography>
                    </Box>

                    {/* Notifications */}
                    <Box
                      data-tour="notifications"
                      onClick={() => navigate('/notifications')}
                      sx={{ ...navItemSx, color: location.pathname === '/notifications' ? 'primary.main' : 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        <Badge
                          badgeContent={unreadNotificationsCount || null}
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.6rem',
                              height: 15,
                              minWidth: 15,
                              padding: '0 4px',
                            },
                          }}
                        >
                          <NotificationsTwoTone sx={{ fontSize: 22 }} />
                        </Badge>
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Alerts</Typography>
                    </Box>

                    {/* Theme Toggle */}
                    <Box
                      data-tour="theme"
                      onClick={toggleTheme}
                      sx={{ ...navItemSx, color: 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        {mode === 'light' ? <Brightness4 sx={{ fontSize: 22 }} /> : <Brightness7 sx={{ fontSize: 22 }} />}
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Theme</Typography>
                    </Box>

                    {/* Help/Tour */}
                    <Box
                      onClick={handleStartTour}
                      sx={{ ...navItemSx, color: 'text.secondary' }}
                    >
                      <Box sx={iconRowSx}>
                        <HelpOutline sx={{ fontSize: 22 }} />
                      </Box>
                      <Typography variant="caption" sx={labelSx}>Tour</Typography>
                    </Box>
                  </>
                )}

                {/* Profile Avatar */}
                <Box
                  data-tour="profile"
                  onClick={handleProfileMenuOpen}
                  sx={{ ...navItemSx, color: 'text.secondary' }}
                >
                  <Box sx={iconRowSx}>
                    <Badge
                      badgeContent={isMobile ? (followRequests.filter(req => req.status === 'pending').length || null) : null}
                      color="error"
                      variant="dot"
                    >
                      <Avatar
                        src={user?.profilePicture?.url}
                        sx={{ width: 22, height: 22, fontSize: '0.75rem', bgcolor: 'primary.main' }}
                      >
                        {user?.name?.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                  </Box>
                  {!isMobile && (
                    <Typography variant="caption" sx={labelSx}>Me</Typography>
                  )}
                </Box>
              </>
            );
          })()}
        </Box>

        {/* Profile Menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                overflow: 'visible',
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.role || 'Role'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email || 'Email'}
            </Typography>
          </Box>
          <Divider />

          {/* Mobile-only menu items */}
          {isMobile && (
            <>
              <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/talky'); }}>
                <HomeTwoTone sx={{ mr: 2, fontSize: 20, color: location.pathname === '/talky' ? 'primary.main' : 'inherit' }} />
                Home
              </MenuItem>
              <MenuItem onClick={() => { handleProfileMenuClose(); handleNetworkClick(); }}>
                <GroupTwoTone sx={{ mr: 2, fontSize: 20, color: location.pathname === '/network' ? 'primary.main' : 'inherit' }} />
                My Network
              </MenuItem>
              <MenuItem onClick={(e) => { handleProfileMenuClose(); handleRequestsMenuOpen(e); }}>
                <Badge
                  badgeContent={followRequests.filter(req => req.status === 'pending').length || null}
                  color="error"
                  sx={{ mr: 2 }}
                >
                  <PersonAddTwoTone sx={{ fontSize: 20 }} />
                </Badge>
                <Box sx={{ ml: 1 }}>Follow Requests</Box>
              </MenuItem>
              <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/notifications'); }}>
                <Badge badgeContent={unreadNotificationsCount || null} color="error" sx={{ mr: 2 }}>
                  <NotificationsTwoTone sx={{ fontSize: 20, color: location.pathname === '/notifications' ? 'primary.main' : 'inherit' }} />
                </Badge>
                <Box sx={{ ml: 1 }}>Notifications</Box>
              </MenuItem>
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">Theme</Typography>
                <ThemeToggle />
              </Box>
              <Divider />
            </>
          )}

          <MenuItem onClick={handleProfileClick}>
            <PersonTwoTone sx={{ mr: 2, fontSize: 20 }} />
            My Profile
          </MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>
            <SettingsTwoTone sx={{ mr: 2, fontSize: 20 }} />
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <LogoutTwoTone sx={{ mr: 2, fontSize: 20 }} />
            Logout
          </MenuItem>
        </Menu>

        {/* Follow Requests Menu */}
        <Menu
          id="requests-menu"
          anchorEl={requestsAnchorEl}
          open={isRequestsMenuOpen}
          onClose={handleRequestsMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                overflow: 'visible',
                mt: 1.5,
                minWidth: 320,
                maxWidth: 400,
                maxHeight: 400,
                borderRadius: 2,
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Follow Requests
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {requestsLoading && followRequests.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : followRequests.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No follow requests
                </Typography>
              </Box>
            ) : (
              followRequests.map((request) => (
                <MenuItem
                  key={request._id}
                  onClick={() => handleRequestClick(request.requester._id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    minHeight: 'auto',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '1rem',
                        bgcolor: 'primary.main',
                      }}
                      src={request.requester.profilePictureUrl || undefined}
                    >
                      {request.requester.name?.charAt(0)?.toUpperCase() || 
                       request.requester.email.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {request.requester.name || request.requester.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {new Date(request.requestedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {request.status === 'pending' && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
            {hasMoreRequests && (
              <Box sx={{ p: 1 }}>
                <Box
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    loadMoreRequests();
                  }}
                  disabled={requestsLoading}
                  sx={{
                    width: '100%',
                    p: 1,
                    border: 'none',
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    '&:disabled': {
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                    },
                    borderRadius: 1,
                  }}
                >
                  {requestsLoading ? 'Loading...' : 'Load More'}
                </Box>
              </Box>
            )}
          </Box>
        </Menu>

        {/* Logout Confirmation Dialog */}
        <ConfirmDialog
          open={logoutDialogOpen}
          title="Confirm Logout"
          message="Are you sure you want to logout? You will need to login again to access your account."
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={handleLogoutConfirm}
          onCancel={handleLogoutCancel}
          severity="warning"
        />

        {/* Tour Component */}
        <Tour
          steps={tourSteps}
          open={tourOpen}
          onClose={handleCloseTour}
        />
      </Toolbar>
    </AppBar>
  );
};